import { AbstractControl } from '@angular/forms';

/** The Receipt Type options of an expect fund. The text is the value stored in the database. */
export const ReceiptType = {
    FasterPayment: 'Faster Payment',
    Chaps: 'CHAPS',
} as const;

/** The prefix of the Transaction Reference for each request type and payment method. */
const TransactionReferencePrefix = {
    PaymentChaps: 'TTO',
    PaymentFasterPayment: 'FPO',
    ExpectFundChaps: 'TTI',
    ExpectFundFasterPayment: 'FPI',
    Transfer: 'TFR',
} as const;

type TransactionDate = Date | string | null | undefined;

/** The day of the month as two digits (the 5th is '05'), or an empty string when there is no valid date. */
function dayOfMonth(date: TransactionDate): string {
    if (!date) return '';

    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';

    return String(parsed.getDate()).padStart(2, '0');
}

function withDay(prefix: string, date: TransactionDate): string {
    const day = dayOfMonth(date);

    // Without a transaction date the reference would be incomplete, so none is generated.
    return day ? `${prefix}${day}` : '';
}

/** Payment request: TTO (CHAPS) or FPO (Faster Payment), then the day of the transaction date. */
export function buildPaymentTransactionReference(isChaps: boolean, date: TransactionDate): string {
    return withDay(isChaps ? TransactionReferencePrefix.PaymentChaps : TransactionReferencePrefix.PaymentFasterPayment, date);
}

/** Expect fund: TTI (CHAPS) or FPI (Faster Payment) from the Receipt Type, then the day of the transaction date. */
export function buildExpectFundTransactionReference(receiptType: string | null | undefined, date: TransactionDate): string {
    switch (receiptType) {
        case ReceiptType.Chaps:
            return withDay(TransactionReferencePrefix.ExpectFundChaps, date);
        case ReceiptType.FasterPayment:
            return withDay(TransactionReferencePrefix.ExpectFundFasterPayment, date);
        default:
            // No receipt type chosen yet, so the prefix is not known.
            return '';
    }
}

/** Transfer request: TFR, then the day of the transaction date. */
export function buildTransferTransactionReference(date: TransactionDate): string {
    return withDay(TransactionReferencePrefix.Transfer, date);
}

/**
 * Puts the generated reference into the control unless a person has typed or saved their own.
 * Returns the reference to pass back as `lastGenerated` on the next call.
 */
export function applyGeneratedTransactionReference(
    control: AbstractControl | null,
    generated: string,
    lastGenerated: string
): string {
    if (!control || !generated) return lastGenerated;

    const current: string = control.value ?? '';

    // A value that is neither empty nor the one generated last time was typed (or saved earlier) by a person, so keep it.
    if (current && current !== lastGenerated) return lastGenerated;

    control.setValue(generated);
    return generated;
}
